/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { Fragment, useState } from 'react';
import { View, Text, TouchableOpacity, ActivityIndicator } from 'react-native';
import * as Clipboard from 'expo-clipboard';
import { TicketListRow } from '../../../../shared/ticket/TicketListRow'
import { settingsStyles } from '../../../../settings/screens/settingsScreen.styles';
import { ticketColors } from '../../../../shared/theme/ticketColors'
import { spacing } from '../../../../shared/theme/spacing';
import { useOperationalConfirm } from '../../../../shared/ui/useOperationalConfirm';
import { empPermStyles } from './employeePermissions.styles';
import {
  invitationUrlFromToken,
  invitationExpiryRelative,
  type InvitationPermissions,
} from '../../../application/employeeInvitations';
import { invitationRoleLabel } from './EmployeeRolePresets';
import { MODULE_LABELS_ES } from './permissionUiLabels';
import { formatLocalDate } from '@shared/vueltoDateTime';
import type { EmployeePermissionsVm } from './useEmployeePermissions';
import { resolveBranchDisplayName } from './useEmployeePermissions';
import type { ModuleKey } from '../../../../shell/session/store/sessionTypes';

function invitationPermissionSummary(permissions: InvitationPermissions | null | undefined): string | null {
  if (!permissions) return null;
  const labels = Object.entries(permissions)
    .filter(([, p]) => p?.canView)
    .map(([module]) => MODULE_LABELS_ES[module as ModuleKey] ?? module)
    .sort((a, b) => a.localeCompare(b, 'es'));
  if (labels.length === 0) return null;
  const top = labels.slice(0, 3);
  const extra = labels.length - top.length;
  return extra > 0 ? `${top.join(', ')} +${extra}` : top.join(', ');
}

type Props = {
  vm: EmployeePermissionsVm;
  onNavigateInvite: () => void;
};

function invitationStatusTone(
  status: string,
  isExpired: boolean
): 'ok' | 'bad' | 'warn' | 'muted' {
  if (status === 'accepted') return 'ok';
  if (status === 'cancelled') return 'bad';
  if (status === 'pending' && isExpired) return 'muted';
  if (status === 'pending') return 'warn';
  return 'muted';
}

export function EmployeeInvitationsTab({ vm, onNavigateInvite }: Props) {
  const {
    invitations,
    invitationsLoading,
    invitationsError,
    branchNameById,
    onCancelInvitation,
    onResendInvitation,
    canInvite,
  } = vm;
  const [copyFeedbackId, setCopyFeedbackId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const { confirm, ConfirmModal } = useOperationalConfirm();

  return (
    <View style={{ gap: spacing.md }}>
      {invitationsLoading ? (
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, padding: spacing.md }}>
          <ActivityIndicator size="small" color={ticketColors.gold} />
          <Text style={{ ...settingsStyles.caption }}>Cargando invitaciones…</Text>
        </View>
      ) : invitationsError ? (
        <Text style={empPermStyles.errorText}>{invitationsError}</Text>
      ) : invitations.length === 0 ? (
        <View style={empPermStyles.emptyState}>
          <Text style={empPermStyles.emptyTitle}>Sin invitaciones</Text>
          <Text style={empPermStyles.emptySubtitle}>
            Cuando invites a alguien, aparecerá acá con su estado.
          </Text>
        </View>
      ) : (
        <Fragment>
          {invitations.map((inv) => {
            const isExpired = inv.expires_at ? new Date(inv.expires_at) < new Date() : false;
            const statusLabel =
              inv.status === 'accepted'
                ? 'Aceptada'
                : inv.status === 'cancelled'
                  ? 'Cancelada'
                  : inv.status === 'rejected'
                    ? 'Rechazada'
                    : inv.status === 'pending'
                      ? isExpired
                        ? 'Expirada'
                        : 'Pendiente'
                      : String(inv.status);
            const branchLabel =
              inv.branch_ids.length === 0
                ? null
                : inv.branch_ids
                    .map((bid) => resolveBranchDisplayName(branchNameById, bid))
                    .join(', ');
            const inviteUrl = invitationUrlFromToken(inv.token);
            const canCopyInviteLink = inv.status === 'pending' && !isExpired && Boolean(inviteUrl);
            const canResend =
              canInvite &&
              (inv.status === 'pending' || inv.status === 'cancelled') &&
              Boolean(onResendInvitation);
            const canCancel = canInvite && inv.status === 'pending' && Boolean(onCancelInvitation);
            const permSummary = invitationPermissionSummary(inv.permissions);
            const expiryLabel = invitationExpiryRelative(inv.expires_at);
            const subtitle = [inv.full_name, invitationRoleLabel(inv.role), permSummary]
              .filter(Boolean)
              .join(' · ') || null;
            const metaParts = [
              formatLocalDate(inv.invited_at),
              branchLabel ? `Sucursales: ${branchLabel}` : null,
              expiryLabel,
            ].filter(Boolean) as string[];
            const rowBusy = busyId === inv.id;

            return (
              <TicketListRow
                key={inv.id}
                title={inv.email}
                subtitle={subtitle}
                meta={metaParts.join(' · ') || null}
                statusLabel={statusLabel}
                statusTone={invitationStatusTone(inv.status, isExpired)}
                testID="perfil.team.invitations.row"
                footer={
                  canCopyInviteLink || canResend || canCancel ? (
                    <View style={empPermStyles.invitationActionsRow}>
                      {canCopyInviteLink && inviteUrl ? (
                        <TouchableOpacity
                          style={empPermStyles.invitationCopyButton}
                          onPress={async () => {
                            try {
                              await Clipboard.setStringAsync(inviteUrl);
                              setCopyFeedbackId(inv.id);
                              setTimeout(
                                () => setCopyFeedbackId((cur) => (cur === inv.id ? null : cur)),
                                2500
                              );
                            } catch {
                              setCopyFeedbackId(null);
                            }
                          }}
                          accessibilityLabel={`Copiar link de invitación para ${inv.email}`}
                          accessibilityRole="button"
                        >
                          <Text style={empPermStyles.invitationCopyButtonLabel}>Copiar link</Text>
                        </TouchableOpacity>
                      ) : null}
                      {canResend ? (
                        <TouchableOpacity
                          style={empPermStyles.invitationCopyButton}
                          disabled={rowBusy}
                          onPress={async () => {
                            const ok = await confirm({
                              title:
                                isExpired || inv.status === 'cancelled'
                                  ? 'Reenviar invitación'
                                  : 'Reenviar mail',
                              message:
                                isExpired || inv.status === 'cancelled'
                                  ? `Generamos un link nuevo y se lo mandamos a ${inv.email}.`
                                  : `Le volvemos a mandar el mail a ${inv.email}.`,
                              confirmLabel: 'Reenviar',
                            });
                            if (!ok) return;
                            setBusyId(inv.id);
                            void onResendInvitation(inv.id).finally(() => setBusyId(null));
                          }}
                          accessibilityLabel={`Reenviar invitación a ${inv.email}`}
                          accessibilityRole="button"
                          testID="perfil.team.invitations.resend"
                        >
                          <Text style={empPermStyles.invitationCopyButtonLabel}>
                            {rowBusy ? '…' : 'Reenviar'}
                          </Text>
                        </TouchableOpacity>
                      ) : null}
                      {canCancel ? (
                        <TouchableOpacity
                          style={empPermStyles.invitationDangerButton}
                          disabled={rowBusy}
                          onPress={async () => {
                            const ok = await confirm({
                              title: 'Cancelar invitación',
                              message: `¿Cancelás la invitación a ${inv.email}? El link deja de servir.`,
                              confirmLabel: 'Cancelar invitación',
                              destructive: true,
                            });
                            if (!ok) return;
                            setBusyId(inv.id);
                            void onCancelInvitation(inv.id).finally(() => setBusyId(null));
                          }}
                          accessibilityLabel={`Cancelar invitación de ${inv.email}`}
                          accessibilityRole="button"
                          testID="perfil.team.invitations.cancel"
                        >
                          <Text style={empPermStyles.invitationDangerButtonLabel}>Cancelar</Text>
                        </TouchableOpacity>
                      ) : null}
                      {canInvite ? (
                        <TouchableOpacity
                          style={empPermStyles.invitationCopyButton}
                          onPress={onNavigateInvite}
                          accessibilityLabel="Editar invitando de nuevo con el mismo correo"
                          accessibilityRole="button"
                          testID="perfil.team.invitations.edit"
                        >
                          <Text style={empPermStyles.invitationCopyButtonLabel}>Editar</Text>
                        </TouchableOpacity>
                      ) : null}
                      {copyFeedbackId === inv.id ? (
                        <Text style={empPermStyles.invitationCopyFeedback}>Link copiado.</Text>
                      ) : null}
                    </View>
                  ) : null
                }
              />
            );
          })}
        </Fragment>
      )}

      {ConfirmModal}
    </View>
  );
}
